import { useTranslation } from 'react-i18next';
import { HpIcon, type IconName } from '@hp/ui';
import type { View } from './filters';
import { deltaOf, metricText, type Fmt } from './format';
import type { Metric, PeriodReport } from './use-report';

type KpiKey = keyof PeriodReport['kpis'];

interface KpiSpec {
  key: KpiKey;
  icon: IconName;
  render: (fmt: Fmt, v: number) => string;
  lowerIsBetter?: boolean;
  kind?: 'percent' | 'points' | 'times';
}

const MANAGER: KpiSpec[] = [
  { key: 'investment', icon: 'wallet', render: (f, v) => f.compactCzk(v) },
  { key: 'reservationValue', icon: 'trendingUp', render: (f, v) => f.compactCzk(v) },
  { key: 'roas', icon: 'target', render: (f, v) => f.times(v), kind: 'times' },
  { key: 'leads', icon: 'users', render: (f, v) => f.int(v) },
  { key: 'qualified', icon: 'mousePointerClick', render: (f, v) => f.int(v) },
  { key: 'reserved', icon: 'badgeCheck', render: (f, v) => f.int(v) },
];
const MARKETING: KpiSpec[] = [
  { key: 'impressions', icon: 'target', render: (f, v) => f.count(v) },
  { key: 'clicks', icon: 'mousePointerClick', render: (f, v) => f.count(v) },
  { key: 'ctr', icon: 'trendingUp', render: (f, v) => f.pct(v, 2), kind: 'points' },
  { key: 'cpc', icon: 'wallet', render: (f, v) => f.czk(v), lowerIsBetter: true },
  { key: 'leads', icon: 'users', render: (f, v) => f.int(v) },
  { key: 'cpql', icon: 'badgeCheck', render: (f, v) => f.czk(v), lowerIsBetter: true },
];

/** Six KPI cards of a view (spec 6 + 6), each with its definition tooltip and the change against the comparison period. */
export function KpiGrid({
  view,
  current,
  previous,
  previousComplete,
  compareOff,
  fmt,
}: {
  view: View;
  current: PeriodReport;
  previous: PeriodReport | null;
  previousComplete: boolean;
  compareOff: boolean;
  fmt: Fmt;
}) {
  const { t } = useTranslation(['performance', 'common']);
  const specs = view === 'manager' ? MANAGER : MARKETING;
  return (
    <div className="pa-kpis">
      {specs.map((s) => {
        const m: Metric = current.kpis[s.key];
        const delta =
          previous && previousComplete
            ? deltaOf(m, previous.kpis[s.key], fmt, { lowerIsBetter: s.lowerIsBetter, kind: s.kind })
            : null;
        const label = t(`dashboard.kpi.${s.key}`);
        return (
          <article key={s.key} className="pa-kpi hp-card">
            <div className="pa-kpi-top">
              <span className="pa-kpi-icon">
                <HpIcon name={s.icon} />
              </span>
              <details className="pa-tooltip">
                <summary aria-label={label}>
                  <HpIcon name="info" />
                </summary>
                <p>{t(`dashboard.kpi.${s.key}Hint`)}</p>
              </details>
            </div>
            <div className="pa-kpi-label">{label}</div>
            <strong
              title={
                m.value == null && m.reason
                  ? t(`dashboard.kpi.reason.${m.reason}`, { defaultValue: m.reason })
                  : undefined
              }
            >
              {metricText(m, (v) => s.render(fmt, v))}
            </strong>
            {m.value == null ? (
              <small>{t(`dashboard.kpi.status.${m.status}`, { defaultValue: m.status })}</small>
            ) : compareOff ? (
              <small>{t('dashboard.kpi.noComparison')}</small>
            ) : delta ? (
              <small>
                <b className={delta.tone}>{delta.text}</b>
              </small>
            ) : (
              <small>{t('dashboard.kpi.noPrevious')}</small>
            )}
          </article>
        );
      })}
    </div>
  );
}
